// games/OneWordStory.jsx — FSM-aware. Extend vs Fresh restart. UUID-safe.

import { useState, useEffect, useRef } from 'react';
import { useGameSession } from '../useGameSession';

const TURN_TIMER = 15;

export default function OneWordStory({ sessionId, myPlayerId, onBack, onRestart }) {
 const { session, loading, error, submitMove, submitSkip, confirmReady, resign, restart } = useGameSession(sessionId);
  const [submitting, setSubmitting] = useState(false);
  const [word, setWord]             = useState('');
  const [timeLeft, setTimeLeft]     = useState(TURN_TIMER);
  const timerRef                    = useRef(null);
  const inputRef                    = useRef(null);

  const currentTurn   = session?.currentTurn;
  const fsmState      = session?.fsm_state;
  const invitedBy     = session?.invited_by;
  const nextSessionId = session?.nextSessionId;
  const iAmInvitedBy  = String(invitedBy) === String(myPlayerId);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (fsmState !== 'ACTIVE') return;
    setTimeLeft(TURN_TIMER);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          if (currentTurn === myPlayerId) submitSkip(myPlayerId);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentTurn, fsmState, myPlayerId, submitSkip]);

  // Focus input when it's my turn
  useEffect(() => {
    if (fsmState === 'ACTIVE' && currentTurn === myPlayerId) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [currentTurn, fsmState, myPlayerId]);

  // The invited_player watches for nextSessionId from the /state poll.
  // When the inviting player has restarted, transition to the new session.
  useEffect(() => {
    if (fsmState !== 'COMPLETED') return;
    if (!nextSessionId) return;
    if (iAmInvitedBy) return; // inviting player handles their own transition
    if (onRestart) onRestart(nextSessionId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsmState, nextSessionId, iAmInvitedBy]);

  if (loading) return <div className="ows-msg">Connecting…</div>;
  if (error)   return <div className="ows-msg err">Connection error. Retrying…</div>;
  if (!session) return null;

  const { fsm_state, game_state: gs, ready_confirmed } = session;
  const isMyTurn = String(session.currentTurn) === String(myPlayerId);

  // UUID-safe score lookup
  const myScore  = gs?.scores?.[String(myPlayerId)] ?? 0;
  const oppScore = Object.entries(gs?.scores ?? {})
    .find(([id]) => String(id) !== String(myPlayerId))?.[1] ?? 0;

  const iHaveConfirmed = !!ready_confirmed?.[myPlayerId];
  const storyWords     = gs?.words ?? [];
const storyText      = storyWords.map(w => w.word).join(' ');
  const wordCount      = storyWords.length;
  const wordLimit      = gs?.wordLimit ?? 30;

  async function handleSubmit() {
    const w = word.trim();
    if (!w || !isMyTurn || submitting) return;if ((w.match(/ /g) || []).length > 1) return; // max two words
    setSubmitting(true);
    setWord('');
    await submitMove(myPlayerId, { type: 'word', word: w });
    setSubmitting(false);
  }

 function handleClose() {
    if (onBack) onBack();
  }

  // ── READY_PENDING ─────────────────────────────────────────────────────────
  if (fsm_state === 'READY_PENDING') {
    return (
      <>
        <style>{OWS_STYLES}</style>
        <div className="ows-phase">
          <div className="ows-icon">📖</div>
          <h3>Ready for One Word Story?</h3>
          <p className="ows-sub">Both players must confirm within 10 seconds.</p>
          {!iHaveConfirmed
            ? <button className="ows-btn" onClick={() => confirmReady(myPlayerId)}>I'm Ready</button>
            : <p className="ows-waiting">Waiting for the other player…</p>
          }
          <button className="ows-resign" onClick={() => resign(myPlayerId)}>Not now</button>
        </div>
      </>
    );
  }

  // ── CANCELLED ─────────────────────────────────────────────────────────────
  if (fsm_state === 'CANCELLED') {
    const isReadyTimeout = session.cancel_reason === 'ready_timeout';
    return (
      <>
        <style>{OWS_STYLES}</style>
        <div className="ows-phase">
          <div className="ows-icon">❌</div>
          <h3>Game cancelled</h3>
          <p>
            {isReadyTimeout
              ? "A player wasn't ready in time — please retry"
              : (session.cancel_reason || 'Game was cancelled').replace(/_/g, ' ')}
          </p>
          <button className="ows-btn" onClick={handleClose}>Try Another Game</button>
        </div>
      </>
    );
  }

  // ── COMPLETED ─────────────────────────────────────────────────────────────
  if (fsm_state === 'COMPLETED') {
    return (
      <>
        <style>{OWS_STYLES}</style>
        <div className="ows-phase">
          <div className="ows-icon">🎭</div>
          <h3>Story complete!</h3>
          <div className="ows-story-box">
            <p className="ows-story-text">{storyText || '(no words yet)'}</p>
          </div>
          <div className="ows-final-scores">
            <div className="ows-score-item">
              <span className="lbl">You</span>
              <span className="val">{myScore} words</span>
            </div>
            <div className="ows-score-sep">·</div>
            <div className="ows-score-item">
              <span className="lbl">Them</span>
              <span className="val">{oppScore} words</span>
            </div>
          </div>
          {session.end_reason && (
            <p className="ows-end-reason">{session.end_reason.replace(/_/g, ' ')}</p>
          )}
          <div className="ows-restart-row">
            <button className="ows-btn-half" onClick={async () => {
              const newSessionId = await restart('story', 'extend');
              if (newSessionId && onRestart) onRestart(newSessionId);
            }}>
              Extend story<br/><span className="ows-btn-sub">+10 more words</span>
            </button>
            <button className="ows-btn-half secondary" onClick={async () => {
              const newSessionId = await restart('story', 'fresh');
              if (newSessionId && onRestart) onRestart(newSessionId);
            }}>
              Fresh story<br/><span className="ows-btn-sub">brand new start</span>
            </button>
          </div>
          <button className="ows-resign" onClick={handleClose}>Back to Games</button>
        </div>
      </>
    );
  }

  // ── ACTIVE ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{OWS_STYLES}</style>
      <div className="ows-wrap">
        <div className="ows-scores">
          <div className={`ows-score ${isMyTurn ? 'active' : ''}`}>
            <span className="lbl">Your words</span>
            <span className="val">{myScore}</span>
          </div>
          <div className="ows-wordcount">{wordCount}/{wordLimit}</div>
          <div className={`ows-score ${!isMyTurn ? 'active' : ''}`}>
            <span className="lbl">Their words</span>
            <span className="val">{oppScore}</span>
          </div>
        </div>

        <div className="ows-card">
          <div className="ows-meta">
            <span className="ows-label">One Word Story</span>
            <div className={`ows-timer ${timeLeft <= 4 ? 'danger' : ''}`}>{timeLeft}</div>
          </div>

          <div className="ows-story">
            {storyWords.length === 0
              ? <span className="ows-placeholder">Start the story…</span>
              : storyWords.map((w, i) => (
                      <span key={i} className={`ows-word ${i === storyWords.length - 1 ? 'last' : ''} ${w.playerId === null ? 'starter' : String(w.playerId) === String(myPlayerId) ? 'mine' : 'theirs'}`}>{w.word} </span>
                    ))
            }
          </div>

          <div className={`ows-turn-lbl ${isMyTurn ? '' : 'waiting'}`}>
            {isMyTurn ? 'Your turn — add one word' : 'Waiting for their word…'}
          </div>

          {isMyTurn && (
            <div className="ows-input-row">
              <input
                ref={inputRef}
                className="ows-input"
                placeholder="one word…"
                value={word}
                maxLength={24}
onChange={e => { const val = e.target.value; if ((val.match(/ /g) || []).length > 1) return; setWord(val.replace(/\s\s+/g, ' ')); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
              />
              <button className="ows-submit" onClick={handleSubmit} disabled={!word.trim() || submitting}>
                →
              </button>
            </div>
          )}
        </div>

        <button className="ows-resign" onClick={() => resign(myPlayerId)}>Resign</button>
      </div>
    </>
  );
}

const OWS_STYLES = `
  .ows-wrap { font-family:'Plus Jakarta Sans',sans-serif; max-width:420px; margin:0 auto; padding:16px; }
  .ows-msg  { text-align:center; padding:40px; color:#888888; font-size:0.85rem; }
  .ows-msg.err { color:#FE7210; }

  .ows-phase { background:#1A1A1A; border:1px solid #2A2A2A; border-radius:16px;
    padding:28px 20px; text-align:center; display:flex; flex-direction:column;
    align-items:center; gap:12px; }
  .ows-icon  { font-size:2.4rem; }
  .ows-phase h3 { color:#EFEFEF; font-size:1.2rem; margin:0; font-weight:600; }
  .ows-phase p  { font-size:0.82rem; color:#888888; margin:0; }
  .ows-waiting  { font-size:0.78rem; color:#555555; animation:ows-pulse 1.4s ease-in-out infinite; }
  @keyframes ows-pulse { 0%,100%{opacity:1}50%{opacity:0.35} }
  .ows-sub      { font-size:0.78rem; color:#888888; }

  .ows-scores { display:flex; justify-content:space-between; align-items:center;
    background:#1A1A1A; border:1px solid #2A2A2A; border-radius:12px;
    padding:12px 20px; margin-bottom:14px; }
  .ows-score      { text-align:center; }
  .ows-score .lbl { display:block; font-size:0.65rem; color:#888888; text-transform:uppercase; letter-spacing:0.1em; }
  .ows-score .val { display:block; font-size:1.4rem; font-weight:700; color:#EFEFEF; }
  .ows-score.active .val { color:#FBBC15; }
  .ows-wordcount { font-size:0.68rem; color:#555555; text-transform:uppercase; letter-spacing:0.08em; }

  .ows-card { background:#1A1A1A; border:1px solid #2A2A2A; border-radius:16px; padding:20px; margin-bottom:12px; }
  .ows-meta { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
  .ows-label { font-size:0.72rem; color:#0D9DA5; background:#0A1F22; border:1px solid #0D9DA5; padding:4px 10px; border-radius:20px; }
  .ows-timer { font-size:1.3rem; font-weight:700; color:#FBBC15;
    width:38px; height:38px; border-radius:50%; border:2px solid #FBBC15;
    display:flex; align-items:center; justify-content:center; }
  .ows-timer.danger { border-color:#FE7210; color:#FE7210; }

  .ows-story { min-height:80px; font-size:1rem; color:#EFEFEF; line-height:1.7;
    padding:10px; background:#0D0D0D; border-radius:10px; margin-bottom:14px; }
  .ows-placeholder { color:#555555; font-style:italic; }
  .ows-word.last   { font-weight:700; }
  .ows-word.mine   { color:#0D9DA5; }
  .ows-word.theirs { color:#FBBC15; }
  .ows-word.starter { color:#888888; }

  .ows-turn-lbl { text-align:center; font-size:0.78rem; font-weight:600; color:#FBBC15; margin-bottom:12px; }
  .ows-turn-lbl.waiting { color:#555555; }

  .ows-input-row { display:flex; gap:8px; }
  .ows-input { flex:1; background:#2A2A2A; border:1px solid #2A2A2A; border-radius:10px;
    color:#EFEFEF; font-size:0.92rem; padding:10px 14px; outline:none; }
  .ows-input::placeholder { color:#555555; }
  .ows-input:focus { border-color:#FBBC15; }
  .ows-submit { background:#FBBC15; color:#5A3A00; border:none; border-radius:10px;
    width:44px; font-size:1.2rem; font-weight:700; cursor:pointer; }
  .ows-submit:disabled { opacity:0.3; cursor:default; }

  .ows-story-box { background:#0D0D0D; border:1px solid #2A2A2A; border-radius:10px;
    padding:14px; width:100%; text-align:left; }
  .ows-story-text { font-size:0.9rem; color:#EFEFEF; line-height:1.7; margin:0; }

  .ows-final-scores { display:flex; gap:16px; align-items:center; justify-content:center;
    background:#2A2A2A; border-radius:10px; padding:12px 20px; width:100%; }
  .ows-score-item { text-align:center; }
  .ows-score-item .lbl { display:block; font-size:0.62rem; color:#888888; text-transform:uppercase; }
  .ows-score-item .val { font-size:1rem; font-weight:700; color:#FBBC15; }
  .ows-score-sep { font-size:0.7rem; color:#555555; }
  .ows-end-reason { font-size:0.7rem; color:#555555; text-transform:uppercase; letter-spacing:0.08em; }

  .ows-restart-row { display:flex; gap:8px; width:100%; }
  .ows-btn-half { flex:1; background:#FBBC15; color:#5A3A00; border:none; border-radius:10px;
    padding:12px 10px; font-weight:700; font-size:0.82rem; cursor:pointer; line-height:1.3; }
  .ows-btn-half.secondary { background:#2A2A2A; color:#EFEFEF; border:1px solid #2A2A2A; }
  .ows-btn-half:hover { opacity:0.85; }
  .ows-btn-sub { font-size:0.68rem; font-weight:400; display:block; }

  .ows-btn { background:#FBBC15; color:#5A3A00; border:none; border-radius:10px;
    padding:12px 28px; font-weight:700; font-size:0.88rem; cursor:pointer; width:100%; }
  .ows-btn:hover { opacity:0.85; }
  .ows-resign { display:block; width:100%; background:transparent; color:#555555;
    border:1px solid #2A2A2A; border-radius:10px; padding:9px; font-size:0.8rem; cursor:pointer; margin-top:6px; }
  .ows-resign:hover { color:#f43f5e; border-color:#f43f5e; }
`;
